<script>
  import Layout from '../components/Layout.svelte';
  import { changePassword } from '../stores/auth.js';
  import { t, tImmediate } from '../i18n/index.js';
  import { KeyRound } from '../lib/icons/index.js';
  import ErrorBanner from '../components/ErrorBanner.svelte';
  import SuccessBanner from '../components/SuccessBanner.svelte';

  const MIN_LENGTH = 8;

  let oldPassword = '';
  let newPassword = '';
  let confirmPassword = '';
  let error = '';
  let success = false;
  let loading = false;

  // Validation used to happen only on submit, so the user learned the rule
  // after filling in all three fields and pressing the button. The rule is now
  // stated permanently under the field, and each check reports itself as soon
  // as the field it concerns has been left (touched), never while the user is
  // still typing the first characters.
  let touchedNew = false;
  let touchedConfirm = false;

  $: tooShort = newPassword.length > 0 && newPassword.length < MIN_LENGTH;
  $: mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
  $: newPasswordError = touchedNew && tooShort ? tImmediate('changePassword.tooShort') : '';
  $: confirmError = touchedConfirm && mismatch ? tImmediate('changePassword.mismatch') : '';

  async function handleSubmit(e) {
    e.preventDefault();
    error = '';
    success = false;
    touchedNew = true;
    touchedConfirm = true;

    if (newPassword.length < MIN_LENGTH) {
      error = tImmediate('changePassword.tooShort');
      return;
    }
    if (newPassword !== confirmPassword) {
      error = tImmediate('changePassword.mismatch');
      return;
    }

    loading = true;
    const result = await changePassword(oldPassword, newPassword);
    loading = false;

    if (result.success) {
      success = true;
      oldPassword = '';
      newPassword = '';
      confirmPassword = '';
      touchedNew = false;
      touchedConfirm = false;
    } else {
      error = result.error;
    }
  }
</script>

<Layout>
  <div class="max-w-lg mx-auto">
    <div class="flex items-center gap-3 mb-6">
      <h1 class="section-title mb-0">{$t('changePassword.title')}</h1>
    </div>

    <div class="card p-6">
      {#if success}
        <SuccessBanner message={$t('changePassword.successMessage')} />
      {/if}

      <ErrorBanner {error} raw />

      <form on:submit={handleSubmit} class="space-y-5">
        <div>
          <label for="oldPassword" class="block eyebrow eyebrow-lg mb-2" style="color: var(--color-text-muted);">
            {$t('changePassword.currentPassword')}
          </label>
          <input id="oldPassword" type="password" bind:value={oldPassword} required autocomplete="current-password"
            class="input" />
        </div>

        <div>
          <label for="newPassword" class="block eyebrow eyebrow-lg mb-2" style="color: var(--color-text-muted);">
            {$t('changePassword.newPassword')}
          </label>
          <input id="newPassword" type="password" bind:value={newPassword} required autocomplete="new-password"
            minlength={MIN_LENGTH}
            on:blur={() => touchedNew = true}
            aria-describedby="newPassword-rule"
            aria-invalid={newPasswordError ? 'true' : undefined}
            class="input"
            style="border-color: {newPasswordError ? 'var(--color-danger)' : 'var(--color-tint-medium)'}; color: var(--color-text-body);"
            placeholder={$t('changePassword.minLengthPlaceholder')} />
          <p id="newPassword-rule" class="mt-1 text-xs" style="color: var(--color-text-muted);">
            {$t('changePassword.rule', { n: MIN_LENGTH })}
          </p>
          {#if newPasswordError}
            <p class="mt-1 text-xs text-danger" role="status">{newPasswordError}</p>
          {/if}
        </div>

        <div>
          <label for="confirmPassword" class="block eyebrow eyebrow-lg mb-2" style="color: var(--color-text-muted);">
            {$t('changePassword.confirmPassword')}
          </label>
          <input id="confirmPassword" type="password" bind:value={confirmPassword} required autocomplete="new-password"
            on:blur={() => touchedConfirm = true}
            aria-invalid={confirmError ? 'true' : undefined}
            class="input"
            style="border-color: {confirmError ? 'var(--color-danger)' : 'var(--color-tint-medium)'}; color: var(--color-text-body);" />
          {#if confirmError}
            <p class="mt-1 text-xs text-danger" role="status">{confirmError}</p>
          {:else if confirmPassword.length > 0 && !mismatch}
            <p class="mt-1 text-xs text-success" role="status">{$t('changePassword.match')}</p>
          {/if}
        </div>

        <button type="submit" disabled={loading}
          class="btn btn-primary w-full gap-2">
          {#if loading}
            <span class="animate-pulse">{$t('app.updating')}</span>
          {:else}
            <KeyRound size={18} />
            <span>{$t('changePassword.submit')}</span>
          {/if}
        </button>
      </form>
    </div>
  </div>
</Layout>
